/**
 * RÉGLAGES RH COMMUNS — fonctions PURES de l'écran (suivi RH, lot 5, retours de Mathilde du
 * 26/10/2026 : listes de choix modifiables, valeurs hors liste, couleurs du thème).
 *
 * Une seule source de vérité : le serveur (GET /api/suivi-rh/reglages) sert le « réglage
 * effectif » = valeurs de départ écrites dans le code + surcharges de la RH. Ce fichier ne
 * fait que :
 *   - donner les valeurs de départ de REPLI (serveur muet, tests sans fournisseur) ;
 *   - fusionner la réponse du serveur avec ce repli (une liste absente garde le repli) ;
 *   - dire le libellé, la couleur et la variable CSS d'une valeur (retirées comprises) ;
 *   - proposer les valeurs actives (sans les retirées) ;
 *   - faire l'APERÇU du contraste (le serveur reste le seul juge : il refuse ou avertit).
 *
 * Les clés ne changent jamais : seul le libellé se renomme. Une valeur retirée reste
 * affichée telle quelle sur les lignes qui la portent (jamais effacée ni remplacée).
 */
import {
  STATUTS_RH, RENOUVELLEMENTS, EMPLOIS, DECISIONS_PROPOSEES, MOTIFS_PROPOSES,
} from './suiviRh';
import { MOTIFS_SORTIE } from '../constants/sortieSalarie';

// ── Mise à plat (même règle que utils/periode_essai.plat côté serveur) ──────────────
/** Majuscules, sans accents, « _ » lu comme une espace, espaces normalisées. */
export function plat(texte) {
  return String(texte ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

const slug = (t) => plat(t).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

// ── Couleurs de départ (palette douce de Mathilde, = index.css) ─────────────────────
export const FOND_REFERENCE = '#141416';
export const ACCENT_DEPART = '#C9A84C';
export const COULEUR_AJOUT_DEFAUT = '#8a8a96';
export const COULEURS_STATUTS_DEPART = {
  sorti: '#7d6b9e',
  en_cours_de_sortie: '#b39bc8',
  a_verifier: '#c58fb4',
  arret_pe_ou_renouvellement: '#d2b96a',
  urgent: '#c9625f',
  annule: '#6fa3bd',
  fait_en_paie: '#8bb07a',
};
export const PREREGLAGES_ACCENT = [
  { nom: 'Doré', couleur: '#C9A84C' },
  { nom: 'Bleu', couleur: '#5b8def' },
  { nom: 'Vert', couleur: '#46b26b' },
  { nom: 'Cuivre', couleur: '#c47a3a' },
];
/** Seuils du contraste contre le fond des panneaux (#141416), mêmes valeurs que le serveur. */
export const SEUILS_CONTRASTE = { statut_refus: 3.0, avertissement: 4.5, accent_refus: 4.5 };

// ── Les listes ──────────────────────────────────────────────────────────────────────
export const ORDRE_LISTES = [
  'statut_rh', 'decision', 'motif_sortie', 'renouvellement', 'emploi',
  'obligations', 'types_preuve', 'griefs', 'rappels_regle',
];
export const LIBELLES_LISTES = {
  statut_rh: 'Statut RH',
  decision: 'Décision',
  motif_sortie: 'Motif de sortie',
  renouvellement: 'Renouvellement',
  emploi: 'Emploi',
  obligations: 'Obligations (courriers)',
  types_preuve: 'Types de preuve (courriers)',
  griefs: 'Griefs (courriers)',
  rappels_regle: 'Règles rappelées (courriers)',
};
/** Ce que chaque liste accepte, dit dans le bloc (le serveur fait foi par `regles_listes`). */
export const NOTES_LISTES = {
  statut_rh: 'Clé fixe : seul le libellé change. Les 7 statuts de départ ne se retirent jamais (lus par les alertes, l’onglet Sorties, les courriers et l’import). Un statut ajouté n’a aucun effet sur les alertes ni les compteurs ; il ne se retire que lorsqu’aucune ligne ne le porte.',
  decision: 'Ajouter, renommer, retirer librement. Une décision déjà écrite sur une ligne reste telle quelle.',
  motif_sortie: 'Les 9 motifs de la fiche et « Fin essai », « Fin essai salarié » (lus par l’alerte) sont figés. Les ajouts de la RH ne sont pas proposés dans la sortie de la fiche.',
  renouvellement: 'Liste fermée : libellés seuls modifiables (les gestes, la frise et l’alerte J-7 lisent ces valeurs).',
  emploi: 'ADS, CYNO et AUTRE ne se retirent pas (le modèle du contrat les propose) ; les ajouts se retirent librement.',
  obligations: 'Liste du bloc « Faits et preuves » des courriers. « Autre » ne se retire pas.',
  types_preuve: 'Liste du bloc « Faits et preuves » des courriers.',
  griefs: 'Liste du bloc « Faits et preuves » des courriers.',
  rappels_regle: 'Règles rappelées dans les courriers. « Autre » ne se retire pas.',
};

const VARIABLES_DEPART = Object.fromEntries(STATUTS_RH.map((s) => [s.cle, s.variable]));

/** La variable CSS d'un statut : celle de index.css pour les 7 de départ, --rh-statut-<clé> sinon. */
export function variableDuStatut(cle) {
  if (VARIABLES_DEPART[cle]) return VARIABLES_DEPART[cle];
  const propre = String(cle || '').toLowerCase().replace(/[^a-z0-9_-]/g, '');
  return propre ? `--rh-statut-${propre}` : '--rh-aucun';
}

const entree = (cle, libelle, extra = {}) => ({
  cle,
  libelle,
  libelle_depart: libelle,
  anciens_libelles: [],
  origine: 'depart',
  renommable: true,
  retirable: true,
  retire: false,
  retire_le: null,
  retire_par: null,
  ...extra,
});

function motifsDepart() {
  const figes = MOTIFS_SORTIE.map((m) => entree(m.code, m.label, { origine: 'figee', renommable: false, retirable: false }));
  const finEssai = [
    entree('fin_essai', 'Fin essai', { origine: 'figee', renommable: false, retirable: false }),
    entree('fin_essai_salarie', 'Fin essai salarié', { origine: 'figee', renommable: false, retirable: false }),
  ];
  const vus = new Set([...figes, ...finEssai].map((e) => plat(e.libelle)));
  const cles = new Set([...figes, ...finEssai].map((e) => e.cle));
  const autres = MOTIFS_PROPOSES.filter((m) => !vus.has(plat(m)))
    .map((m) => entree(cles.has(slug(m)) ? `propose_${slug(m)}` : slug(m), m));
  return [...figes, ...finEssai, ...autres];
}

/** Le réglage de départ (repli) : même forme que GET /api/suivi-rh/reglages. */
export const REGLAGES_DEPART = Object.freeze({
  listes: {
    statut_rh: STATUTS_RH.map((s) => entree(s.cle, s.libelle, {
      retirable: false, couleur: COULEURS_STATUTS_DEPART[s.cle], variable: s.variable,
    })),
    decision: DECISIONS_PROPOSEES.map((d) => entree(slug(d), d)),
    motif_sortie: motifsDepart(),
    renouvellement: RENOUVELLEMENTS.map((r) => entree(r.cle, r.libelle, { retirable: false })),
    emploi: EMPLOIS.map((e) => entree(e, e, { retirable: false })),
    obligations: [],
    types_preuve: [],
    griefs: [],
    rappels_regle: [],
  },
  regles_listes: {
    statut_rh: { ajout: true, retrait: 'si_aucune_ligne' },
    decision: { ajout: true, retrait: 'libre' },
    motif_sortie: { ajout: true, retrait: 'libre' },
    renouvellement: { ajout: false, retrait: 'jamais' },
    emploi: { ajout: true, retrait: 'libre' },
    obligations: { ajout: true, retrait: 'libre' },
    types_preuve: { ajout: true, retrait: 'libre' },
    griefs: { ajout: true, retrait: 'libre' },
    rappels_regle: { ajout: true, retrait: 'libre' },
  },
  couleurs: {
    accent: ACCENT_DEPART,
    accent_depart: ACCENT_DEPART,
    prereglages: PREREGLAGES_ACCENT,
    statuts: STATUTS_RH.map((s) => ({
      cle: s.cle,
      couleur: COULEURS_STATUTS_DEPART[s.cle],
      couleur_depart: COULEURS_STATUTS_DEPART[s.cle],
      variable: s.variable,
    })),
    fond_reference: FOND_REFERENCE,
    seuils: SEUILS_CONTRASTE,
  },
  version_listes: 0,
  version_couleurs: 0,
  maj_listes: null,
  maj_couleurs: null,
  calcule_le: null,
  droits: { peut_modifier: false },
  depart: true,
});

/**
 * Le réglage effectif : la réponse du serveur, complétée par le repli liste par liste
 * (une liste absente ou mal formée garde ses valeurs de départ, jamais une liste vide
 * inventée). `depart: true` quand rien n'a été lu.
 */
export function fusionnerReglages(reponse) {
  if (!reponse || typeof reponse !== 'object' || !reponse.listes) return REGLAGES_DEPART;
  const listes = {};
  for (const nom of ORDRE_LISTES) {
    const lue = reponse.listes[nom];
    listes[nom] = Array.isArray(lue) ? lue.filter((e) => e && e.cle) : REGLAGES_DEPART.listes[nom];
  }
  for (const nom of Object.keys(reponse.listes)) {
    if (!(nom in listes) && Array.isArray(reponse.listes[nom])) listes[nom] = reponse.listes[nom];
  }
  const couleursLues = reponse.couleurs && typeof reponse.couleurs === 'object' ? reponse.couleurs : {};
  return {
    ...REGLAGES_DEPART,
    ...reponse,
    listes,
    regles_listes: { ...REGLAGES_DEPART.regles_listes, ...(reponse.regles_listes || {}) },
    couleurs: {
      ...REGLAGES_DEPART.couleurs,
      ...couleursLues,
      statuts: Array.isArray(couleursLues.statuts) ? couleursLues.statuts : REGLAGES_DEPART.couleurs.statuts,
      seuils: { ...SEUILS_CONTRASTE, ...(couleursLues.seuils || {}) },
    },
    droits: { peut_modifier: !!(reponse.droits && reponse.droits.peut_modifier) },
    depart: false,
  };
}

// ── Lecture d'une liste ─────────────────────────────────────────────────────────────
const liste = (l) => (Array.isArray(l) ? l : []);

/** Les valeurs proposées dans une saisie : sans les retirées. */
export const proposables = (l) => liste(l).filter((e) => e && !e.retire);

/** L'entrée d'une clé (retirées comprises), ou null. */
export const entreeDe = (l, cle) => (cle ? liste(l).find((e) => e && e.cle === cle) || null : null);

/** Le libellé d'une clé, retirées comprises ; la clé telle quelle si elle est inconnue (jamais inventée). */
export function libelleDe(l, cle) {
  const e = entreeDe(l, cle);
  return e ? e.libelle : (cle || '');
}

/**
 * La clé ACTIVE dont le libellé (ou un ancien libellé) égale le texte après mise à plat ;
 * '' sinon. Aucun rapprochement par ressemblance : « Fin CDD » et « Fin de CDD » restent deux.
 */
export function cleDuLibelle(l, texte) {
  const p = plat(texte);
  if (!p) return '';
  const e = proposables(l).find((x) => plat(x.libelle) === p
    || liste(x.anciens_libelles).some((a) => plat(a) === p));
  return e ? e.cle : '';
}

// ── Couleurs ────────────────────────────────────────────────────────────────────────
export const estHexa = (v) => /^#[0-9a-fA-F]{6}$/.test(String(v || ''));

function luminance(hex) {
  const h = String(hex).slice(1);
  const canal = (i) => {
    const c = parseInt(h.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * canal(0) + 0.7152 * canal(2) + 0.0722 * canal(4);
}

/** Rapport de contraste WCAG (arrondi au centième) ; null si l'une des couleurs n'est pas #rrggbb. */
export function contraste(hex, fond = FOND_REFERENCE) {
  if (!estHexa(hex) || !estHexa(fond)) return null;
  const a = luminance(hex);
  const b = luminance(fond);
  const r = (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  return Math.round(r * 100) / 100;
}

/**
 * APERÇU du jugement (le serveur juge) : statut refusé sous 3,0:1, averti entre 3,0 et 4,5 ;
 * accent refusé sous 4,5:1 (il sert de texte et de fond de bouton à texte #111).
 */
export function jugerCouleur(hex, genre = 'statut', seuils = SEUILS_CONTRASTE, fond = FOND_REFERENCE) {
  const ratio = contraste(hex, fond);
  if (ratio === null) return { ratio: null, niveau: 'invalide' };
  const s = { ...SEUILS_CONTRASTE, ...(seuils || {}) };
  if (genre === 'accent') return { ratio, niveau: ratio < s.accent_refus ? 'refus' : 'ok' };
  if (ratio < s.statut_refus) return { ratio, niveau: 'refus' };
  if (ratio < s.avertissement) return { ratio, niveau: 'avertissement' };
  return { ratio, niveau: 'ok' };
}

/** « 3,92:1 » */
export const libelleRatio = (r) => (Number.isFinite(r) ? `${String(r.toFixed(2)).replace('.', ',')}:1` : '—');

/**
 * Les variables CSS posées par le fournisseur : UNIQUEMENT des --rh-* (accent --rh-or, les 7
 * statuts de départ, --rh-statut-<clé> des ajouts, retirés compris car des lignes les portent).
 * Jamais --accent, --primary, --gold-* ni les variables du thème par utilisateur.
 */
export function variablesCss(reglages) {
  const r = reglages || REGLAGES_DEPART;
  const vars = {};
  const accent = r.couleurs && estHexa(r.couleurs.accent) ? r.couleurs.accent : ACCENT_DEPART;
  vars['--rh-or'] = accent;
  for (const s of liste(r.couleurs && r.couleurs.statuts)) {
    if (s && s.cle && estHexa(s.couleur)) vars[s.variable || variableDuStatut(s.cle)] = s.couleur;
  }
  for (const e of liste(r.listes && r.listes.statut_rh)) {
    const v = e.variable || variableDuStatut(e.cle);
    if (!(v in vars) && estHexa(e.couleur)) vars[v] = e.couleur;
  }
  return Object.fromEntries(Object.entries(vars).filter(([k]) => k.startsWith('--rh-')));
}

/** La couleur affichée d'un statut (réglage effectif), '' si inconnue. */
export function couleurDuStatut(reglages, cle) {
  const r = reglages || REGLAGES_DEPART;
  const s = liste(r.couleurs && r.couleurs.statuts).find((x) => x && x.cle === cle);
  if (s && estHexa(s.couleur)) return s.couleur;
  const e = entreeDe(r.listes && r.listes.statut_rh, cle);
  return e && estHexa(e.couleur) ? e.couleur : '';
}

// ── Messages des refus du serveur (charte 1.3 : jamais un code brut à l'écran) ───────
export const MESSAGES_REFUS = {
  couleur_invalide: 'Couleur illisible : choisir une couleur dans la palette.',
  statut_inconnu: 'Ce statut n’existe plus dans la liste : la page relit le réglage.',
  contraste_insuffisant: 'Contraste trop faible sur le fond noir : couleur refusée.',
  modifie_entre_temps: 'Modifié entre-temps par un collègue : le réglage est relu, refaites la saisie.',
  liste_inconnue: 'Liste inconnue.',
  liste_fermee: 'Liste fermée : on ne peut que renommer ses libellés.',
  doublon_libelle: 'Ce libellé est déjà dans la liste.',
  libelle_fige: 'Libellé figé : il est lu par le logiciel (fiche, alerte), il ne se renomme pas.',
  valeur_lue_par_le_logiciel: 'Valeur lue par le logiciel (alertes, onglet Sorties, courriers, import) : elle ne se retire pas. Renommez-la si besoin.',
  valeur_portee: 'Des lignes portent encore ce statut : réaffectez-les d’abord.',
  donnees_invalides: 'Saisie refusée par le serveur.',
};

/** Le refus structuré du serveur { code, message, … } d'une erreur axios, ou null. */
export function refusDe(erreur) {
  const d = erreur && erreur.response && erreur.response.data && erreur.response.data.detail;
  if (d && typeof d === 'object' && d.code) return d;
  return null;
}

/** Le texte d'un refus : le message du serveur s'il en donne un, sinon le nôtre, sinon un repli. */
export function texteRefus(erreur, repli = 'Refusé par le serveur.') {
  const d = refusDe(erreur);
  if (d) {
    const base = d.message || MESSAGES_REFUS[d.code] || repli;
    if (d.code === 'contraste_insuffisant' && Number.isFinite(d.ratio)) {
      return `${base} (${libelleRatio(d.ratio)}, minimum ${libelleRatio(d.minimum)})`;
    }
    return base;
  }
  return repli;
}
